import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  indexFromScroll,
  progressFromScroll,
  swipeTabId,
  swipeTabPanelId,
  tabIndexForKey,
} from '@/components/shared/swipe-tabs.utils'
import { cn } from '@/lib/utils'

export type SwipeTabSpec<T extends string> = {
  value: T
  label: string
  icon?: LucideIcon
  count?: number | null
}

type SwipeTabsProps<T extends string> = {
  /** tab と tabpanel を紐づける id の接頭辞。画面内で一意な文字列を渡す */
  idPrefix: string
  ariaLabel: string
  tabs: readonly SwipeTabSpec<T>[]
  activeValue: T
  onChange: (value: T) => void
  renderPanel: (value: T) => ReactNode
  /** 各パネル (縦スクロール領域) に追加するクラス */
  panelClassName?: string
}

// スクロールイベントがこの時間来なければ止まったとみなす。
// scrollend は Safari の対応が新しいため使わない。
const SCROLL_SETTLE_MS = 120

/**
 * SwipeTabs - 上部タブ + 左右スワイプで切り替えるパネル
 *
 * パネルを横に並べて CSS scroll-snap でページ送りする。指への追従や慣性はブラウザに任せ、
 * スクロールが止まった位置でタブを確定する (途中のタブを経由して onChange しない)。
 * タブの下線はスクロール位置に合わせて動く。
 * タブ操作や activeValue の変更時は、そのパネルまでスムーズにスクロールする。
 */
export const SwipeTabs = <T extends string>({
  idPrefix,
  ariaLabel,
  tabs,
  activeValue,
  onChange,
  renderPanel,
  panelClassName,
}: SwipeTabsProps<T>) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const settleTimerRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const touchingRef = useRef(false)
  const mountedRef = useRef(false)

  const activeIndex = Math.max(
    tabs.findIndex((tab) => tab.value === activeValue),
    0,
  )
  const [progress, setProgress] = useState(activeIndex)

  // スクロール系のハンドラを作り直さずに最新の props を参照するため ref に持つ
  const latestRef = useRef({ tabs, activeIndex, activeValue, onChange })
  useEffect(() => {
    latestRef.current = { tabs, activeIndex, activeValue, onChange }
  })

  // タブ操作や URL の変更で activeIndex が変わったら、そのパネルまでスクロールする。
  // スワイプで切り替えた場合はすでにその位置にいるので何もしない。
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const behavior: ScrollBehavior = mountedRef.current ? 'smooth' : 'instant'
    mountedRef.current = true
    const left = activeIndex * scroller.clientWidth
    if (Math.abs(scroller.scrollLeft - left) < 1) return
    scroller.scrollTo({ left, behavior })
  }, [activeIndex])

  // 画面回転などで幅が変わったら、表示中のパネルの位置に合わせ直す
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const observer = new ResizeObserver(() => {
      scroller.scrollTo({ left: latestRef.current.activeIndex * scroller.clientWidth, behavior: 'instant' })
    })
    observer.observe(scroller)
    return () => observer.disconnect()
  }, [])

  useEffect(
    () => () => {
      if (settleTimerRef.current != null) window.clearTimeout(settleTimerRef.current)
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  const settle = useCallback(() => {
    const scroller = scrollerRef.current
    // 指を止めたまま押さえている間は確定しない (確定するとパネルが指から逃げる)
    if (!scroller || touchingRef.current) return
    const { tabs: currentTabs, activeValue: currentValue, onChange: change } = latestRef.current
    const next = currentTabs.at(indexFromScroll(scroller.scrollLeft, scroller.clientWidth, currentTabs.length))
    if (next && next.value !== currentValue) change(next.value)
  }, [])

  const scheduleSettle = useCallback(() => {
    if (settleTimerRef.current != null) window.clearTimeout(settleTimerRef.current)
    settleTimerRef.current = window.setTimeout(settle, SCROLL_SETTLE_MS)
  }, [settle])

  const handleScroll = () => {
    const scroller = scrollerRef.current
    if (!scroller) return
    if (frameRef.current == null) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        setProgress(progressFromScroll(scroller.scrollLeft, scroller.clientWidth, latestRef.current.tabs.length))
      })
    }
    scheduleSettle()
  }

  const handleTouchEnd = () => {
    touchingRef.current = false
    scheduleSettle()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const nextIndex = tabIndexForKey(event.key, index, tabs.length)
    if (nextIndex === null) return
    const nextTab = tabs.at(nextIndex)
    if (!nextTab) return
    event.preventDefault()
    tabRefs.current.at(nextIndex)?.focus()
    onChange(nextTab.value)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div role="tablist" aria-label={ariaLabel} className="relative flex shrink-0 border-b">
        {tabs.map(({ value, label, icon: Icon, count }, index) => {
          const isActive = value === activeValue
          return (
            <button
              key={value}
              ref={(el) => {
                tabRefs.current[index] = el
              }}
              type="button"
              role="tab"
              id={swipeTabId(idPrefix, value)}
              aria-selected={isActive}
              aria-controls={swipeTabPanelId(idPrefix, value)}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'flex min-w-0 flex-1 items-center justify-center gap-1 px-1 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {Icon && <Icon className="size-4 shrink-0" />}
              <span className="truncate">{label}</span>
              {count != null && <span className="text-xs opacity-70">({count})</span>}
            </button>
          )
        })}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 h-0.5 bg-primary"
          style={{ width: `${100 / Math.max(tabs.length, 1)}%`, transform: `translateX(${progress * 100}%)` }}
        />
      </div>

      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        onTouchStart={() => {
          touchingRef.current = true
        }}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map(({ value }) => (
          <div
            key={value}
            role="tabpanel"
            id={swipeTabPanelId(idPrefix, value)}
            aria-labelledby={swipeTabId(idPrefix, value)}
            // 隣のパネルはスワイプ用に描画しているだけなので、フォーカスや読み上げの対象から外す
            inert={value !== activeValue}
            className={cn('h-full w-full shrink-0 snap-start snap-always overflow-y-auto', panelClassName)}
          >
            {renderPanel(value)}
          </div>
        ))}
      </div>
    </div>
  )
}
